import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Spin,
  message,
  Input,
  Button,
  Modal,
  Tooltip, // Add this import
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom"; // Add this import
import "./UserList.css";

const UserList = () => {
  const navigate = useNavigate(); // Add this hook
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState({
    FirmName: "",
    ContactName: "",
    ContactNumber: "",
    City: "",
    State: "",
  });

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusValues, setStatusValues] = useState({
    IsActive: false,
    IsApproved: false,
    ShowProductStatus: false,
    AllowtoOrder: false,
  });

  useEffect(() => {
    fetchEmployeeAndLoad();
  }, []);

  // Fetch the EmployeeID from the account detail then load users scoped to that employee
  const fetchEmployeeAndLoad = async () => {
    const tokenString = localStorage.getItem("token");
    try {
      const res = await axios.get(
        global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        { headers: { Authorization: "Bearer " + tokenString } }
      );
      getListOfUsersByEmployee(res.data.EmployeeID);
    } catch {
      setLoading(false);
    }
  };

  const getListOfUsersByEmployee = async (employeeId) => {
    setLoading(true);
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.get(
        global.config.APIURL.BaseURL +
          "api/User/GetListOfUsersbyEmployee?EmployeeID=" +
          employeeId,
        {
          headers: {
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      setUsers(response.data || []);
    } catch (error) {
      message.error("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search fields
  const filteredUsers = users.filter((user) => {
    return (
      user.FirmName?.toLowerCase().includes(search.FirmName.toLowerCase()) &&
      user.ContactName?.toLowerCase().includes(
        search.ContactName.toLowerCase()
      ) &&
      (user.ContactNumber || "")
        .toString()
        .toLowerCase()
        .includes(search.ContactNumber.toLowerCase()) &&
      user.City?.toLowerCase().includes(search.City.toLowerCase()) &&
      user.State?.toLowerCase().includes(search.State.toLowerCase())
    );
  });

  // Handle Modify button click
  const handleModify = (user) => {
    setSelectedUser(user);
    setStatusValues({
      IsActive: user.IsActive,
      IsApproved: user.IsApproved,
      ShowProductStatus: user.ShowProductStatus,
      AllowtoOrder: user.AllowtoOrder,
    });
    setModalVisible(true);
  };

  // Update the handleViewDetails function to navigate to UserDetails page
  const handleViewDetails = (user) => {
    // Navigate to UserDetails page with user ID parameter
    navigate(`/UserDetails/${user.UserID}`);
  };

  // Enhanced date formatter with error handling
  const formatDate = (dateString) => {
    if (!dateString) return null;

    try {
      const date = new Date(dateString);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      // Format: dd-MM-yyyy hh:mm tt
      const options = {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };

      // Format using toLocaleString with custom options
      const formatted = date.toLocaleString("en-GB", options);

      // Convert to dd-MM-yyyy hh:mm tt format
      const [datePart, timePart] = formatted.split(", ");
      const [day, month, year] = datePart.split("/");

      return `${day}-${month}-${year} ${timePart}`;
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Add this helper function after the formatDate function
  const formatLastActiveTime = (dateString) => {
    if (!dateString) {
      return {
        text: "Never",
        color: "#d32f2f", // Red-ish color but not pure red
      };
    }

    try {
      const lastActiveDate = new Date(dateString);
      const now = new Date();

      if (isNaN(lastActiveDate.getTime())) {
        return {
          text: "Invalid Date",
          color: "#d32f2f",
        };
      }

      const diffInMilliseconds = now - lastActiveDate;
      const diffInHours = diffInMilliseconds / (1000 * 60 * 60);
      const diffInDays = Math.floor(diffInHours / 24);

      let color = "#8c8c8c"; // Default gray
      let displayText = "";

      if (diffInHours < 24) {
        // Less than 24 hours - show time with green color
        color = "#52c41a"; // Green
        displayText = formatDate(dateString);
      } else if (diffInDays < 7) {
        // Less than 7 days - show time with orange color
        color = "#faad14"; // Orange
        displayText = formatDate(dateString);
      } else if (diffInDays < 10) {
        // 7-10 days - show time with light red color
        color = "#ff7875"; // Light red
        displayText = formatDate(dateString);
      } else if (diffInDays < 30) {
        // 10-30 days - show "X days ago"
        color = "#ff4d4f"; // Red
        displayText = `${diffInDays} days ago`;
      } else if (diffInDays < 365) {
        // 30+ days but less than a year - show months and days
        const months = Math.floor(diffInDays / 30);
        const remainingDays = diffInDays % 30;

        color = "#d32f2f"; // Dark red
        if (months === 1) {
          displayText =
            remainingDays > 0
              ? `1 month and ${remainingDays} days ago`
              : `1 month ago`;
        } else {
          displayText =
            remainingDays > 0
              ? `${months} months and ${remainingDays} days ago`
              : `${months} months ago`;
        }
      } else {
        // More than a year
        const years = Math.floor(diffInDays / 365);
        const remainingDays = diffInDays % 365;
        const months = Math.floor(remainingDays / 30);

        color = "#b71c1c"; // Very dark red
        if (years === 1) {
          if (months > 0) {
            displayText = `1 year and ${months} months ago`;
          } else {
            displayText = `1 year ago`;
          }
        } else {
          if (months > 0) {
            displayText = `${years} years and ${months} months ago`;
          } else {
            displayText = `${years} years ago`;
          }
        }
      }

      return {
        text: displayText,
        color: color,
      };
    } catch (error) {
      return {
        text: "Invalid Date",
        color: "#d32f2f",
      };
    }
  };

  const columns = [
    {
      title: (
        <div>
          Firm Name
          <Input
            size="small"
            placeholder="Search Firm Name"
            value={search.FirmName}
            onChange={(e) => setSearch({ ...search, FirmName: e.target.value })}
            style={{ marginTop: 4 }}
            allowClear
          />
        </div>
      ),
      dataIndex: "FirmName",
      key: "FirmName",
      render: (text) => <span style={{ fontWeight: "normal" }}>{text}</span>,
      width: "15%", // Changed from fixed width to percentage
    },
    {
      title: (
        <div>
          Contact Name
          <Input
            size="small"
            placeholder="Search Contact Name"
            value={search.ContactName}
            onChange={(e) =>
              setSearch({ ...search, ContactName: e.target.value })
            }
            style={{ marginTop: 4 }}
            allowClear
          />
        </div>
      ),
      dataIndex: "ContactName",
      key: "ContactName",
      render: (text) => <span style={{ fontWeight: "normal" }}>{text}</span>,
      width: "12%", // Changed from fixed width to percentage
    },
    {
      title: (
        <div>
          Contact Number
          <Input
            size="small"
            placeholder="Search Contact Number"
            value={search.ContactNumber}
            onChange={(e) =>
              setSearch({ ...search, ContactNumber: e.target.value })
            }
            style={{ marginTop: 4 }}
            allowClear
          />
        </div>
      ),
      dataIndex: "ContactNumber",
      key: "ContactNumber",
      render: (text) => <span style={{ fontWeight: "normal" }}>{text}</span>,
      width: "10%", // Changed from fixed width to percentage
    },
    {
      title: (
        <div>
          City
          <Input
            size="small"
            placeholder="Search City"
            value={search.City}
            onChange={(e) => setSearch({ ...search, City: e.target.value })}
            style={{ marginTop: 4 }}
            allowClear
          />
        </div>
      ),
      dataIndex: "City",
      key: "City",
      render: (text) => <span style={{ fontWeight: "normal" }}>{text}</span>,
      width: "8%", // Changed from fixed width to percentage
    },
    {
      title: (
        <div>
          State
          <Input
            size="small"
            placeholder="Search State"
            value={search.State}
            onChange={(e) => setSearch({ ...search, State: e.target.value })}
            style={{ marginTop: 4 }}
            allowClear
          />
        </div>
      ),
      dataIndex: "State",
      key: "State",
      render: (text) => <span style={{ fontWeight: "normal" }}>{text}</span>,
      width: "8%", // Changed from fixed width to percentage
    },
    {
      title: "Registration Time",
      dataIndex: "RegistrationTime",
      key: "RegistrationTime",
      render: (text) => (
        <span style={{ fontWeight: "normal", fontSize: "12px" }}>
          {formatDate(text)}
        </span>
      ),
      width: "10%", // Changed from fixed width to percentage
    },
    {
      title: "Last Active",
      dataIndex: "LastActive",
      key: "LastActive",
      render: (text) => {
        const { text: displayText, color } = formatLastActiveTime(text);

        // Choose icon based on activity recency
        let icon = null;
        if (!text) {
          icon = <CloseOutlined style={{ marginRight: 4, fontSize: 10 }} />;
        } else if (displayText.includes("ago")) {
          icon = (
            <ExclamationCircleOutlined
              style={{ marginRight: 4, fontSize: 10 }}
            />
          );
        } else {
          icon = (
            <ClockCircleOutlined style={{ marginRight: 4, fontSize: 10 }} />
          );
        }

        return (
          <Tooltip
            title={text ? formatDate(text) : "User has never been active"}
          >
            <span
              style={{
                fontWeight: "normal",
                color: color,
                fontSize: "12px",
                backgroundColor:
                  text === null || text === "" ? "#ffebee" : "transparent",
                padding: text === null || text === "" ? "2px 4px" : "0",
                borderRadius: text === null || text === "" ? "3px" : "0",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {icon}
              {displayText}
            </span>
          </Tooltip>
        );
      },
      width: "10%", // Changed from fixed width to percentage
      sorter: (a, b) => {
        if (!a.LastActive && !b.LastActive) return 0;
        if (!a.LastActive) return 1;
        if (!b.LastActive) return -1;
        return new Date(b.LastActive) - new Date(a.LastActive);
      },
    },
    {
      title: "Cart",
      dataIndex: "ProductsinCart",
      key: "ProductsinCart",
      render: (count) => (
        <div style={{ textAlign: "center" }}>
          <span
            style={{
              fontWeight: count > 0 ? "600" : "normal",
              color: count > 0 ? "#1890ff" : "#8c8c8c",
              backgroundColor: count > 0 ? "#e6f7ff" : "transparent",
              padding: count > 0 ? "2px 6px" : "0",
              borderRadius: count > 0 ? "10px" : "0",
              border: count > 0 ? "1px solid #91d5ff" : "none",
              fontSize: "11px",
            }}
          >
            {count || 0}
          </span>
        </div>
      ),
      width: "5%",
      align: "center",
      sorter: (a, b) => (a.ProductsinCart || 0) - (b.ProductsinCart || 0),
    },
    {
      title: "Orders",
      dataIndex: "Orders",
      key: "Orders",
      render: (count) => (
        <div style={{ textAlign: "center" }}>
          <span
            style={{
              fontWeight: count > 0 ? "600" : "normal",
              color: count > 0 ? "#52c41a" : "#8c8c8c",
              backgroundColor: count > 0 ? "#f6ffed" : "transparent",
              padding: count > 0 ? "2px 6px" : "0",
              borderRadius: count > 0 ? "10px" : "0",
              border: count > 0 ? "1px solid #b7eb8f" : "none",
              fontSize: "11px",
            }}
          >
            {count || 0}
          </span>
        </div>
      ),
      width: "5%",
      align: "center",
      sorter: (a, b) => (a.Orders || 0) - (b.Orders || 0),
    },
    {
      title: "Active",
      dataIndex: "IsActive",
      key: "IsActive",
      render: (val) =>
        val ? (
          <CheckOutlined style={{ color: "#52c41a", fontSize: 16 }} />
        ) : (
          <CloseOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
        ),
      width: "5%", // Changed from fixed width to percentage
      align: "center",
    },
    {
      title: "Approved",
      dataIndex: "IsApproved",
      key: "IsApproved",
      render: (val) =>
        val ? (
          <CheckOutlined style={{ color: "#52c41a", fontSize: 16 }} />
        ) : (
          <CloseOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
        ),
      width: "6%", // Changed from fixed width to percentage
      align: "center",
    },
    {
      title: "Show Status",
      dataIndex: "ShowProductStatus",
      key: "ShowProductStatus",
      render: (val) =>
        val ? (
          <CheckOutlined style={{ color: "#52c41a", fontSize: 16 }} />
        ) : (
          <CloseOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
        ),
      width: "7%", // Changed from fixed width to percentage
      align: "center",
    },
    {
      title: "Allow Order",
      dataIndex: "AllowtoOrder",
      key: "AllowtoOrder",
      render: (val) =>
        val ? (
          <CheckOutlined style={{ color: "#52c41a", fontSize: 16 }} />
        ) : (
          <CloseOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
        ),
      width: "7%", // Changed from fixed width to percentage
      align: "center",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
          <Button
            type="default"
            size="small"
            onClick={() => handleViewDetails(record)}
            style={{ fontSize: "11px", padding: "2px 6px" }}
          >
            View
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={() => handleModify(record)}
            style={{ fontSize: "11px", padding: "2px 6px" }}
          >
            Modify
          </Button>
        </div>
      ),
      width: "12%", // Changed from fixed width to percentage
    },
  ];

  return (
    <Card title="User List" style={{ margin: 0, width: "100%" }}>
      {loading ? (
        <Spin size="large" style={{ display: "block", margin: "40px auto" }} />
      ) : (
        <Table
          dataSource={filteredUsers}
          columns={columns}
          rowKey="UserID"
          pagination={{ pageSize: 25 }}
          size="small"
          scroll={{ x: 900 }}
          style={{ fontWeight: "normal", width: "100%" }}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
        />
      )}
      <Modal
        title="Modify User Status"
        open={modalVisible}
        onOk={() => setModalVisible(false)}
        onCancel={() => setModalVisible(false)}
        okText="OK"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        {selectedUser && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* ShowProductStatus */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span>Show Product Status:</span>
              <span
                style={{
                  color: statusValues.ShowProductStatus ? "#52c41a" : "#ff4d4f",
                  fontWeight: 500,
                  minWidth: 80,
                  display: "inline-block",
                }}
              >
                {statusValues.ShowProductStatus ? "Allowed" : "Not Allowed"}
              </span>
              <Button
                type={statusValues.ShowProductStatus ? "default" : "primary"}
                onClick={async () => {
                  const newValue = !statusValues.ShowProductStatus;
                  try {
                    const tokenString = localStorage.getItem("token");
                    await axios.post(
                      global.config.APIURL.BaseURL +
                        "api/User/UpdateProductStatusView",
                      {
                        UserID: selectedUser.UserID,
                        ApproveVal: newValue,
                      },
                      {
                        headers: {
                          Authorization: "Bearer " + tokenString,
                        },
                      }
                    );
                    setStatusValues((prev) => ({
                      ...prev,
                      ShowProductStatus: newValue,
                    }));
                    message.success("Show Product Status updated.");
                    // Refresh the user list after update
                    if (selectedUser && selectedUser.EmployeeID) {
                      await getListOfUsersByEmployee(selectedUser.EmployeeID);
                    } else {
                      // fallback: reload all users (if EmployeeID not available)
                      await getListOfUsersByEmployee();
                    }
                  } catch (error) {
                    message.error("Failed to update Show Product Status.");
                  }
                }}
                size="small"
              >
                {statusValues.ShowProductStatus ? "Do Not Allow" : "Allow"}
              </Button>
            </div>
            {/* AllowtoOrder */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span>Allow to Order:</span>
              <span
                style={{
                  color: statusValues.AllowtoOrder ? "#52c41a" : "#ff4d4f",
                  fontWeight: 500,
                  minWidth: 80,
                  display: "inline-block",
                }}
              >
                {statusValues.AllowtoOrder ? "Allowed" : "Not Allowed"}
              </span>
              <Button
                type={statusValues.AllowtoOrder ? "default" : "primary"}
                onClick={async () => {
                  const newValue = !statusValues.AllowtoOrder;
                  try {
                    const tokenString = localStorage.getItem("token");
                    await axios.post(
                      global.config.APIURL.BaseURL +
                        "api/UserAccount/SetAllowtoOrderStatus",
                      {
                        UserID: selectedUser.UserID,
                        ApproveVal: newValue,
                      },
                      {
                        headers: {
                          Authorization: "Bearer " + tokenString,
                        },
                      }
                    );
                    setStatusValues((prev) => ({
                      ...prev,
                      AllowtoOrder: newValue,
                    }));
                    message.success("Allow to Order status updated.");
                    // Refresh the user list after update
                    if (selectedUser && selectedUser.EmployeeID) {
                      await getListOfUsersByEmployee(selectedUser.EmployeeID);
                    } else {
                      await getListOfUsersByEmployee();
                    }
                  } catch (error) {
                    message.error("Failed to update Allow to Order status.");
                  }
                }}
                size="small"
              >
                {statusValues.AllowtoOrder ? "Do Not Allow" : "Allow"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default UserList;
